import { useEffect, useState } from "react";
import { toast } from "sonner";
import { changePassword } from "@/api/admin";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { errorMessage } from "@/lib/api-client";

const MIN_LENGTH = 12;

export function ChangePasswordDialog({
	open,
	onOpenChange,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}) {
	const [current, setCurrent] = useState("");
	const [next, setNext] = useState("");
	const [confirm, setConfirm] = useState("");
	const [pending, setPending] = useState(false);
	const tooShort = next.length > 0 && next.length < MIN_LENGTH;
	const mismatch = confirm.length > 0 && confirm !== next;
	const valid = current.length > 0 && next.length >= MIN_LENGTH && next === confirm;

	useEffect(() => {
		if (!open) return;
		setCurrent("");
		setNext("");
		setConfirm("");
	}, [open]);

	const submit = async () => {
		setPending(true);
		try {
			await changePassword({ currentPassword: current, newPassword: next });
			toast.success("Password changed");
			onOpenChange(false);
		} catch (error) {
			toast.error("Couldn't change your password", { description: errorMessage(error) });
		} finally {
			setPending(false);
		}
	};

	return (
		<Dialog open={open} onOpenChange={(value) => !pending && onOpenChange(value)}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>Change password</DialogTitle>
					<DialogDescription>
						For your admin account only. App accounts aren't affected.
					</DialogDescription>
				</DialogHeader>
				<form
					className="grid gap-4"
					onSubmit={(e) => {
						e.preventDefault();
						if (valid) void submit();
					}}
				>
					<div className="grid gap-2">
						<Label htmlFor="current-password">Current password</Label>
						<Input
							id="current-password"
							type="password"
							autoComplete="current-password"
							value={current}
							onChange={(e) => setCurrent(e.target.value)}
							autoFocus
							required
						/>
					</div>
					<div className="grid gap-2">
						<Label htmlFor="new-password">New password</Label>
						<Input
							id="new-password"
							type="password"
							autoComplete="new-password"
							value={next}
							maxLength={128}
							aria-invalid={tooShort || undefined}
							onChange={(e) => setNext(e.target.value)}
							required
						/>
						<span className="text-xs text-muted-foreground">At least {MIN_LENGTH} characters.</span>
					</div>
					<div className="grid gap-2">
						<Label htmlFor="confirm-password">Confirm new password</Label>
						<Input
							id="confirm-password"
							type="password"
							autoComplete="new-password"
							value={confirm}
							maxLength={128}
							aria-invalid={mismatch || undefined}
							onChange={(e) => setConfirm(e.target.value)}
							required
						/>
						{mismatch && <span className="text-xs text-destructive">Passwords don't match.</span>}
					</div>
					<DialogFooter>
						<Button
							type="button"
							variant="ghost"
							size="sm"
							disabled={pending}
							onClick={() => onOpenChange(false)}
						>
							Cancel
						</Button>
						<Button type="submit" size="sm" loading={pending} disabled={!valid}>
							Change password
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
